/**
 * Cinematography config — the "shot" of the 3D scene.
 *
 * Every value below is tunable. Defaults are tuned for a "studio table" feel:
 * slight high-angle, ACES filmic tonemap, gentle ground shadow, magenta rim.
 *
 * Want a different vibe? Swap to a preset at the bottom.
 */
export const look = {
  // Camera (defaults / fallback for products without an explicit framing)
  fov: 38,              // 28 = wide/dramatic, 50 = neutral, 80 = telephoto/compressed
  cameraY: 0.3,
  cameraZ: 5.2,
  cameraTiltX: -0.08,

  // Tonemap
  tonemap: 'aces',      // 'aces' | 'reinhard' | 'cineon' | 'linear' | 'none'
  exposure: 1.15,
  envIntensity: 0.9,

  // Lights
  keyLightColor: '#ffffff',
  keyLightIntensity: 1.0,
  rimLightColor: '#ff1493',
  rimLightIntensity: 1.2,

  // Stage
  groundShadow: true,
  spacing: 3.4,

  // Postprocessing
  bloom: true,
  bloomStrength: 0.55,
  bloomRadius: 0.6,
  bloomThreshold: 0.7,

  // Interactivity
  mouseParallax: 0.18,
  activeScale: 1.18,
  activeLift: 0.12,

  // HDRI (optional). Null = procedural RoomEnvironment (instant, no download).
  hdriUrl: null,        // e.g. '/hdri/studio_small_03_1k.hdr'
  hdriAsBackground: false,
};

/**
 * Camera framing presets — picked per-product via product.framing.
 * Each pose is { fov, cameraY, cameraZ, cameraTiltX }.
 * setActive() tweens the camera to the active product's framing.
 */
export const frames = {
  square:    { fov: 38, cameraY: 0.3, cameraZ: 5.2, cameraTiltX: -0.08 },  // default — watches, cubes, balls
  portrait:  { fov: 32, cameraY: 0.4, cameraZ: 6.2, cameraTiltX: -0.06 },  // taller than wide — bottles, perfume, cans
  landscape: { fov: 42, cameraY: 0.6, cameraZ: 7.5, cameraTiltX: -0.10 },  // wider than tall — cars, surfboards, laptops
  tall:      { fov: 30, cameraY: 1.1, cameraZ: 7.0, cameraTiltX: -0.20 },  // very tall — columns, lamps, vases
  hero:      { fov: 26, cameraY: 0.2, cameraZ: 4.5, cameraTiltX: -0.05 },  // tight crop, dramatic
};

/**
 * Material presets — picked per-product via product.material.
 * Apply to BOTH procedural primitives AND GLB-loaded models.
 * Glass/fabric/food require MeshPhysicalMaterial — scene.js handles the upgrade.
 */
export const materials = {
  plastic:   { roughness: 0.4, metalness: 0.05, clearcoat: 0.3, clearcoatRoughness: 0.2 },
  matte:     { roughness: 0.85, metalness: 0.0 },
  metal:     { roughness: 0.18, metalness: 0.95 },
  chrome:    { roughness: 0.05, metalness: 1.0 },
  glass:     { roughness: 0.05, metalness: 0.0, transmission: 0.92, thickness: 0.5, ior: 1.45 },
  fabric:    { roughness: 0.92, metalness: 0.0, sheen: 1.0, sheenRoughness: 0.5, sheenColor: '#ffffff' },
  leather:   { roughness: 0.75, metalness: 0.0, clearcoat: 0.1, clearcoatRoughness: 0.4 },
  food:      { roughness: 0.6, metalness: 0.0, transmission: 0.08, thickness: 0.3 },
  porcelain: { roughness: 0.25, metalness: 0.0, clearcoat: 0.5, clearcoatRoughness: 0.1 },
};

/**
 * Industry presets — drop one of these on top of `look` to retune the whole vibe.
 * They also hint at CSS palette swaps (see `industryCSS` below).
 */
export const industryPresets = {
  lifestyle: {
    // The default — loud, vibrant, playful (POP3D baseline).
  },
  luxury: {
    exposure: 0.95, envIntensity: 1.1, bloomStrength: 0.35,
    rimLightColor: '#d4af37', rimLightIntensity: 0.9,
    keyLightIntensity: 0.85, mouseParallax: 0.1,
    activeScale: 1.1, activeLift: 0.08,
  },
  b2b: {
    exposure: 1.0, tonemap: 'linear', bloom: false,
    rimLightColor: '#4f46e5', rimLightIntensity: 0.6,
    envIntensity: 0.7, mouseParallax: 0.08,
    activeScale: 1.08, activeLift: 0.06,
  },
  wellness: {
    exposure: 1.25, envIntensity: 1.0, bloomStrength: 0.4, bloomRadius: 0.9,
    rimLightColor: '#a8d5ba', rimLightIntensity: 0.8,
    keyLightColor: '#fff8ec', mouseParallax: 0.12,
  },
  beauty: {
    exposure: 1.2, envIntensity: 1.05, bloomStrength: 0.65,
    rimLightColor: '#e8b4b8', rimLightIntensity: 1.0,
    activeScale: 1.22,
  },
  tech: {
    exposure: 1.05, bloomStrength: 0.7, bloomThreshold: 0.55,
    rimLightColor: '#00ffff', keyLightIntensity: 0.7,
    envIntensity: 0.6,
  },
};

/**
 * Industry CSS variable overrides — applied by main.js to <html>.
 * Lets you swap the whole site palette by changing one `industry` field.
 */
export const industryCSS = {
  lifestyle: {
    '--ink': '#0a0a0a', '--paper': '#fff8ec',
    '--accent-1': '#39ff14', '--accent-2': '#ff1493', '--accent-3': '#ffaa00',
  },
  luxury: {
    '--ink': '#0a0807', '--paper': '#f5f0e6',
    '--accent-1': '#d4af37', '--accent-2': '#8b6f47', '--accent-3': '#1a1a1a',
  },
  b2b: {
    '--ink': '#0f172a', '--paper': '#f8fafc',
    '--accent-1': '#4f46e5', '--accent-2': '#0ea5e9', '--accent-3': '#10b981',
  },
  wellness: {
    '--ink': '#1f2937', '--paper': '#f0fdf4',
    '--accent-1': '#a8d5ba', '--accent-2': '#fcd34d', '--accent-3': '#7dd3c0',
  },
  beauty: {
    '--ink': '#3a1c1e', '--paper': '#fff5f5',
    '--accent-1': '#e8b4b8', '--accent-2': '#c9184a', '--accent-3': '#d4af37',
  },
  tech: {
    '--ink': '#0f0f1a', '--paper': '#080808',
    '--accent-1': '#00ffff', '--accent-2': '#bf00ff', '--accent-3': '#39ff14',
  },
};

/**
 * Vibe presets — swap the whole `look` export to one of these to taste.
 */
export const presets = {
  studio: { /* the default */ },
  moody:   { exposure: 0.85, rimLightColor: '#bf00ff', envIntensity: 0.6, keyLightIntensity: 0.7 },
  airy:    { exposure: 1.35, rimLightColor: '#00ffff', envIntensity: 1.1, cameraY: 0.1 },
  cinema:  { fov: 28, exposure: 1.2, tonemap: 'cineon', rimLightColor: '#ffaa00' },
  flat:    { tonemap: 'none', exposure: 1, groundShadow: false, envIntensity: 0.5 },
};
